import { useState, useMemo, createContext, useEffect, useCallback } from 'react';
import omit from 'lodash/omit';

// Functions
import generateInsetStyles from '/functions/generate-inset-styles';
import observeObject from '/functions/observe-object';

// Utils
import { useGlobalEvents } from '@wearetla/tla-essentials-tools/utilities/global-events';

// Context
import { useModals } from '@wearetla/tla-essentials-tools/utilities/modals';
import { useGlobalState } from '@wearetla/tla-essentials-tools/utilities/global-state';
import { useStorage } from '@wearetla/tla-essentials-tools/utilities/storage';

export const ApplicationContext = createContext({
	isApplication: false,
	cameraAllowed: false,
	hasApplicationInterface: false,
	postMessage: () => {},
});

export const ApplicationProvider = ({ children, initialState, supportedAppFeatures: initialSupportedAppFeatures }) => {
	const rawStorageData = useStorage();
	const { safeAreaInsets, setData } = useGlobalState();
	const { triggerEvent } = useGlobalEvents();
	const [isApplication] = useState(initialState);
	const [supportedAppFeatures] = useState(initialSupportedAppFeatures);
	const { openModal } = useModals();
	const [cameraAllowed, setCameraAllowed] = useState(null);

	const storageData = useMemo(() => omit(rawStorageData, ['setData']), [rawStorageData]);
	const hasApplicationInterface = useMemo(() => typeof window !== 'undefined' && !!window?.ReactNativeWebView?.postMessage, [])

	const postMessage = useCallback(
		(type, message) => {
			if (isApplication) {
				window?.ReactNativeWebView?.postMessage?.(JSON.stringify({ type, content: message }));
			}
		},
		[isApplication]
	);

	const appCommEventHandler = (e) => {
		postMessage('log', JSON.stringify({ message: 'App event taken!', detail: e.detail }));
		switch (e?.detail?.action) {
			// Legacy oneSignal integration
			case 'setOneSignalData':
				postMessage('onesignalDetail', e.detail?.userId + ' : ' + e.detail?.pushToken);
				setData('oneSignalPushToken', e.detail?.pushToken ?? '');
				break;
			case 'setPushData':
				postMessage('pushDataDetail', e.detail?.userId + ' : ' + e.detail?.pushToken);
				setData('oneSignalPushToken', e.detail?.pushToken ?? '');
				break;
			case 'setCameraAllowed':
				setCameraAllowed(e.detail?.cameraPermission);
				break;
			case 'setSafeAreaInsets':
				try {
					const parsedInsets = JSON.parse(e.detail?.safeAreaInsets ?? '{}');
					setData('safeAreaInsets', parsedInsets);
				} catch (e) {
					console.warn('Insets parsing error', e);
				}
				break;
			case 'setNfcData':
				if(e.detail?.uid) {
					triggerEvent('onNfcRead', e.detail);
				}
				else {
					console.warn('NFC read error', e);
				}
				break;
			case 'message':
				openModal('message', { message: e.detail.message });
				break;
		}
	};

	const trackLocalStorageChange = (method, key, value) => {
		if (method === 'setItem') {
			postMessage('setLocalStorageKey', { key, value });
		}

		if (method === 'removeItem') {
			postMessage('removeLocalStorageKey', { key });
		}
	};

	useEffect(() => {
		postMessage('cookieStorageUpdate', storageData);
		postMessage('storageUpdate', storageData);
	}, [postMessage, storageData]);

	useEffect(() => {
		if (isApplication) {
			document.addEventListener('applicationComm', appCommEventHandler);

			observeObject(window.localStorage, ['setItem', 'removeItem'], trackLocalStorageChange);

			postMessage('webLoaded');

			window.onerror = function (message, sourcefile, lineno, colno) {
				postMessage(
					'error',
					message + ' - Source: ' + sourcefile + ' Line: ' + lineno + ':' + colno
				);
				return true;
			};
		}
	}, [isApplication]);

	useEffect(() => {
		try {
			const styleTag = document.querySelector('style[data-role="safe-area-insets"]');

			if (isApplication && safeAreaInsets) {
				const insetStyles = generateInsetStyles(safeAreaInsets);
				if (styleTag) {
					styleTag.innerHTML = insetStyles;
				} else {
					const newStyleTag = document.createElement('style');
					newStyleTag.setAttribute('data-role', 'safe-area-insets');
					newStyleTag.innerHTML = insetStyles;
					document.head.prepend(newStyleTag);
				}
			}
		} catch (e) {
			console.log('Safe area inset parsing error', e);
		}
	}, [isApplication, safeAreaInsets, postMessage]);

	const api = useMemo(
		() => ({
			isApplication,
			cameraAllowed,
			supportedAppFeatures,
			hasApplicationInterface,
			postMessage,
		}),
		[isApplication, cameraAllowed, supportedAppFeatures, hasApplicationInterface, postMessage]
	);

	return <ApplicationContext.Provider value={api}>{children}</ApplicationContext.Provider>;
};
